import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { PageHeader } from '../components/Layout';
import { Button, Card } from '../components/ui';
import type { MetricsPayload } from '../lib/metrics';
import { COLORS, RADIUS } from '../theme';

interface AnalystResponse {
  report: string;
  model: string;
  tokensIn?: number;
  tokensOut?: number;
  costUsd?: number;
  latencyMs?: number;
}

export default function AnalystPage() {
  const [metrics, setMetrics] = useState<MetricsPayload | null>(null);
  const [question, setQuestion] = useState('');
  const [status, setStatus]   = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [error, setError]     = useState<string | null>(null);
  const [report, setReport]   = useState<AnalystResponse | null>(null);

  // Prefetch metrics so we can hand them to the analyst — analyst reuses the
  // same aggregated payload the dashboard renders, so the founder is
  // reasoning about the exact numbers on screen.
  useEffect(() => {
    supabase.functions.invoke<MetricsPayload>('admin-metrics', { body: {} })
      .then(({ data }) => setMetrics(data ?? null));
  }, []);

  const ask = useCallback(async () => {
    setStatus('loading');
    setError(null);
    setReport(null);
    const { data, error } = await supabase.functions.invoke<AnalystResponse>('admin-ops-analyst', {
      body: {
        question: question.trim() || undefined,
        metrics:  metrics ?? undefined,
      },
    });
    if (error || !data) {
      setStatus('error');
      setError(error?.message ?? 'Analyst request failed.');
      return;
    }
    setReport(data);
    setStatus('ready');
  }, [question, metrics]);

  return (
    <div>
      <PageHeader title="Analyst" right={
        <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
          Rate-limited to 3 runs / admin / day
        </span>
      } />

      <Card>
        <label style={labelStyle}>Question (optional)</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, 500))}
          placeholder="e.g. Why did Google failures spike yesterday?"
          rows={3}
          style={textareaStyle}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <Button onClick={ask} disabled={status === 'loading' || !metrics}>
            {status === 'loading' ? 'Thinking…' : 'Run analyst'}
          </Button>
          <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
            {metrics ? 'Metrics loaded — will be sent with the question.' : 'Loading metrics…'}
          </span>
        </div>
        {error && <div style={{ color: COLORS.critical, marginTop: 12, fontSize: 13 }}>{error}</div>}
      </Card>

      {report && (
        <Card style={{ marginTop: 20 }}>
          <div style={reportMetaStyle}>
            <span>{report.model}</span>
            {report.tokensIn != null && <span>{report.tokensIn} in / {report.tokensOut} out</span>}
            {report.costUsd != null && <span>${report.costUsd.toFixed(4)}</span>}
            {report.latencyMs != null && <span>{report.latencyMs}ms</span>}
          </div>
          <pre style={reportBodyStyle}>{report.report}</pre>
        </Card>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, color: COLORS.textMuted,
  letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8,
};
const textareaStyle: React.CSSProperties = {
  width: '100%', minHeight: 80, resize: 'vertical' as const,
  background: COLORS.surfaceAlt, color: COLORS.text,
  border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.input,
  padding: 12, fontSize: 14, fontFamily: 'inherit',
};
const reportMetaStyle: React.CSSProperties = {
  display: 'flex', gap: 16, fontSize: 11, color: COLORS.textMuted,
  paddingBottom: 12, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 12,
};
const reportBodyStyle: React.CSSProperties = {
  color: COLORS.text, fontSize: 14, lineHeight: 1.6,
  whiteSpace: 'pre-wrap' as const, wordWrap: 'break-word',
  fontFamily: 'inherit', margin: 0,
};
